import React, { useEffect, useMemo } from 'react';
import { Drawer } from 'components/common';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { FaSave, FaBan } from 'react-icons/fa';
import { createSalesUnit, updateSalesUnit } from 'redux/crm/salesUnitSlice';
import { activeEmployeesByNameDebounce, sitesDebounce } from 'helpers/employeeOptionHelper';
import { debounce, filterOptions } from 'helpers/debounce';
import { CRM_SALES_UNIT_REGION, CRM_SALES_UNIT_TYPE_OF_SALES } from 'app_config';

// react-select must portal its menu above the drawer (z-index 1200) or it gets
// clipped by the drawer's overflow-y-auto body.
const selectStyles = {
  control: (base, state) => ({
    ...base,
    fontSize: '0.8125rem',
    minHeight: '36px',
    borderRadius: '0.5rem',
    borderColor: state.isFocused ? '#6366f1' : '#e2e8f0',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(99,102,241,0.2)' : 'none',
    '&:hover': { borderColor: '#6366f1' },
  }),
  option: (base) => ({ ...base, fontSize: '0.8125rem' }),
  placeholder: (base) => ({ ...base, fontSize: '0.8125rem', color: '#94a3b8' }),
  menuPortal: (base) => ({ ...base, zIndex: 1300 }),
};

const portalProps = {
  classNamePrefix: 'react-select',
  styles: selectStyles,
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed',
};

const Field = ({ label, required, error, children }) => (
  <div className="tw-flex tw-flex-col tw-gap-1">
    <label className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide">
      {label}{required && <span className="tw-text-red-400 tw-ml-0.5">*</span>}
    </label>
    {children}
    {error && <p className="tw-text-xs tw-text-red-500 tw-mt-0.5 tw-mb-0">{error}</p>}
  </div>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  error: PropTypes.string,
  children: PropTypes.node.isRequired,
};

const Section = ({ title, children }) => (
  <div className="tw-mb-5">
    <h6 className="tw-text-[11px] tw-font-bold tw-text-primary-600 tw-uppercase tw-tracking-wider tw-mb-3 tw-pb-2 tw-border-b tw-border-slate-100">
      {title}
    </h6>
    <div className="tw-flex tw-flex-col tw-gap-3.5">{children}</div>
  </div>
);

Section.propTypes = { title: PropTypes.string.isRequired, children: PropTypes.node.isRequired };

const Toggle = ({ checked, onChange, label }) => (
  <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none">
    <span className="tw-relative tw-inline-block tw-w-9 tw-h-5">
      <input type="checkbox" className="tw-sr-only tw-peer" checked={!!checked} onChange={onChange} />
      <span className="tw-absolute tw-inset-0 tw-rounded-xl tw-bg-slate-300 peer-checked:tw-bg-primary-600 tw-transition-colors" />
      <span className="tw-absolute tw-top-0.5 tw-left-0.5 tw-w-4 tw-h-4 tw-bg-white tw-rounded-xl tw-transition-transform peer-checked:tw-translate-x-4" />
    </span>
    <span className="tw-text-sm tw-text-slate-700">{label}</span>
  </label>
);

Toggle.propTypes = { checked: PropTypes.bool, onChange: PropTypes.func.isRequired, label: PropTypes.string.isRequired };

import { INPUT_CLS as inputClass } from 'helpers/formStyles';
const regionOptions = CRM_SALES_UNIT_REGION.map((o) => ({ value: o, label: o }));
const typeOfSalesOptions = CRM_SALES_UNIT_TYPE_OF_SALES.map((o) => ({ value: o, label: o }));

const loadEmployees = (input, cb) => activeEmployeesByNameDebounce(input, cb);
const loadSites = (input, cb) => sitesDebounce(input, cb);
const loadParents = debounce((input, cb) => {
  filterOptions('/crm/sales_units/active_list', input, true).then(cb);
}, 300);

const SalesUnitFormDrawer = ({ show, onHide, salesUnit, onSaved }) => {
  const dispatch = useDispatch();
  const isEdit = !!salesUnit?.id;
  const { control, register, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm();

  // Prefill on open / record change; reset to defaults for create.
  useEffect(() => {
    if (!show) return;
    reset({
      name: salesUnit?.name || '',
      description: salesUnit?.description || '',
      type_of_sales: salesUnit?.type_of_sales || '',
      region: salesUnit?.region || '',
      site_id: salesUnit?.site_id || null,
      parent_id: salesUnit?.parent_id || '',
      sales_admin: salesUnit?.sales_admin || '',
      sales_leader: salesUnit?.sales_leader || '',
      sales_manager: salesUnit?.sales_manager || '',
      sales_tender: salesUnit?.sales_tender || [],
      active: salesUnit ? !!salesUnit.active : true,
      is_sales_office: salesUnit ? !!salesUnit.is_sales_office : true,
    });
  }, [show, salesUnit, reset]);

  const singleOption = (value, label) => (value ? { value, label: label || value } : null);
  const siteDefault = useMemo(
    () => singleOption(salesUnit?.site_id, salesUnit?.site_name),
    [salesUnit]
  );

  const onSubmit = async (data) => {
    try {
      if (isEdit) {
        await dispatch(updateSalesUnit({ salesUnitId: salesUnit.id, data })).unwrap();
        toast.success(`Sales unit "${data.name}" was updated.`);
      } else {
        await dispatch(createSalesUnit(data)).unwrap();
        toast.success(`Sales unit "${data.name}" was created.`);
      }
      onHide();
      onSaved?.();
    } catch (err) {
      toast.error(err?.error || err?.message || 'Failed to save sales unit.');
    }
  };

  const footer = (
    <>
      <button
        type="submit"
        form="sales-unit-form"
        disabled={isSubmitting}
        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded tw-border-0 tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700 disabled:tw-opacity-60 tw-text-sm tw-font-medium tw-cursor-pointer"
      >
        <FaSave size={13} /> {isEdit ? 'Update' : 'Create'}
      </button>
      <button
        type="button"
        onClick={onHide}
        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-font-medium tw-cursor-pointer"
      >
        <FaBan size={13} /> Cancel
      </button>
    </>
  );

  return (
    <Drawer
      show={show}
      onHide={onHide}
      title={isEdit ? `Edit Sales Unit — ${salesUnit.name}` : 'New Sales Unit'}
      width="46%"
      minWidth="460px"
      footer={footer}
    >
      <form id="sales-unit-form" onSubmit={handleSubmit(onSubmit)}>
        <Section title="Information">
          <Field label="Name" required error={errors.name?.message}>
            <input className={inputClass} placeholder="Sales unit name"
              {...register('name', { required: 'Name is required.' })} />
          </Field>

          <Field label="Description" required error={errors.description?.message}>
            <input className={inputClass} placeholder="Description"
              {...register('description', { required: 'Description is required.' })} />
          </Field>

          <Field label="Type of sales" required error={errors.type_of_sales?.message}>
            <Controller control={control} name="type_of_sales"
              rules={{ required: 'Type of sales is required.' }}
              render={({ field }) => (
                <Select {...portalProps} options={typeOfSalesOptions} placeholder="Select..."
                  value={typeOfSalesOptions.find((o) => o.value === field.value) || null}
                  onChange={(opt) => field.onChange(opt?.value)} />
              )} />
          </Field>

          <Field label="Region" required error={errors.region?.message}>
            <Controller control={control} name="region"
              rules={{ required: 'Region is required.' }}
              render={({ field }) => (
                <Select {...portalProps} options={regionOptions} placeholder="Select..."
                  value={regionOptions.find((o) => o.value === field.value) || null}
                  onChange={(opt) => field.onChange(opt?.value)} />
              )} />
          </Field>

          <Field label="Site">
            <Controller control={control} name="site_id"
              render={() => (
                <AsyncSelect {...portalProps} cacheOptions defaultOptions isClearable
                  defaultValue={siteDefault}
                  loadOptions={loadSites} placeholder="Select site..."
                  onChange={(opt) => setValue('site_id', opt?.value ?? null)} />
              )} />
          </Field>

          <Field label="Parent">
            <Controller control={control} name="parent_id"
              render={() => (
                <AsyncSelect {...portalProps} cacheOptions defaultOptions isClearable
                  defaultValue={singleOption(salesUnit?.parent_id)}
                  loadOptions={loadParents} placeholder="Select parent..."
                  onChange={(opt) => setValue('parent_id', opt?.value ?? '')} />
              )} />
          </Field>

          <div className="tw-flex tw-items-center tw-gap-6 tw-pt-1">
            <Controller control={control} name="active"
              render={({ field }) => (
                <Toggle checked={field.value} label="Active" onChange={(e) => field.onChange(e.target.checked)} />
              )} />
            <Controller control={control} name="is_sales_office"
              render={({ field }) => (
                <Toggle checked={field.value} label="Sales Office" onChange={(e) => field.onChange(e.target.checked)} />
              )} />
          </div>
        </Section>

        <Section title="Sales team">
          <Field label="Sales admin">
            <Controller control={control} name="sales_admin"
              render={() => (
                <AsyncSelect {...portalProps} cacheOptions defaultOptions isClearable
                  defaultValue={singleOption(salesUnit?.sales_admin)}
                  loadOptions={loadEmployees} placeholder="Select..."
                  onChange={(opt) => setValue('sales_admin', opt?.value ?? '')} />
              )} />
          </Field>

          <Field label="Sales leader">
            <Controller control={control} name="sales_leader"
              render={() => (
                <AsyncSelect {...portalProps} cacheOptions defaultOptions isClearable
                  defaultValue={singleOption(salesUnit?.sales_leader)}
                  loadOptions={loadEmployees} placeholder="Select..."
                  onChange={(opt) => setValue('sales_leader', opt?.value ?? '')} />
              )} />
          </Field>

          <Field label="Tender">
            <Controller control={control} name="sales_tender"
              render={({ field }) => (
                <AsyncSelect {...portalProps} isMulti cacheOptions defaultOptions
                  loadOptions={loadEmployees} placeholder="Select..."
                  value={(field.value || []).map((v) => ({ value: v, label: v }))}
                  onChange={(opts) => field.onChange((opts || []).map((o) => o.value))} />
              )} />
          </Field>

          <Field label="Chief Rep / GM" required error={errors.sales_manager?.message}>
            <Controller control={control} name="sales_manager"
              rules={{ required: 'Chief Rep / GM is required.' }}
              render={() => (
                <AsyncSelect {...portalProps} cacheOptions defaultOptions isClearable
                  defaultValue={singleOption(salesUnit?.sales_manager)}
                  loadOptions={loadEmployees} placeholder="Select..."
                  onChange={(opt) => setValue('sales_manager', opt?.value ?? '', { shouldValidate: true })} />
              )} />
          </Field>
        </Section>
      </form>
    </Drawer>
  );
};

SalesUnitFormDrawer.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  salesUnit: PropTypes.object,
  onSaved: PropTypes.func,
};

export default SalesUnitFormDrawer;
